import {
  type ScreenEventPayload,
  type TrackEventPayload,
  type UIEventPayload,
} from '@atlassiansox/analytics-web-client';
import { type FieldType } from '@databases/model/field';
import {
  type ActionType,
  type DatabaseFieldType,
  type ExportFileType,
  type PageSubType,
  type ParentPageOption,
  type SelectionMode,
} from '@databases/types/database';
import {
  type DatabaseCalculationType,
  type DatabaseFilterType,
  type DatabaseLayoutType,
} from '@databases/types/database/view';

import { type DatasourceType } from '@canvas/atlassian-service/datasource';

export interface GenericEventData {
  contentId: string;
  databaseContainer: DatabaseContainerType;
}

export enum MacroType {
  DATABASE = 'DATABASE',
  ENTRY = 'ENTRY',
  VALUE = 'VALUE',
}

export interface SendMacroCreatedEventData {
  macroType: MacroType;
  isNew: boolean;
  contentId?: string;
  source?: string;
}

export enum DatabaseContainerType {
  STANDALONE_DATABASE = 'STANDALONE_DATABASE',
  EMBED_DATABASE = 'EMBED_DATABASE',
  STANDALONE_ENTRY = 'STANDALONE_ENTRY',
  EMBED_ENTRY = 'EMBED_ENTRY',
  EMBED_VALUE = 'EMBED_VALUE',
  NONE = 'NONE',
}

export type CustomTrackEventPayload = TrackEventPayload & {
  attributes: {
    databaseContainer: DatabaseContainerType;
  };
};

export type CustomCommentsEventPayload = CustomTrackEventPayload & {
  attributes: {
    commentType?: string;
    parentCommentContentId?: string;
  };
};

export type CustomUIEventPayload = UIEventPayload & {
  attributes: {
    databaseContainer: DatabaseContainerType;
  };
};

export type CustomScreenEventPayload = ScreenEventPayload & {
  attributes?: {
    databaseContainer?: DatabaseContainerType;
  };
};

export interface AddEntryEventData extends GenericEventData {
  source: 'tableLayout' | 'boardLayout' | 'galleryLayout';
}

export interface DatasourcesEventData extends GenericEventData {
  databaseContainer: DatabaseContainerType;
  datasourceType: DatasourceType;
  totalEntries: number;
}

export interface ImportEntriesEventData extends GenericEventData {
  isEmptyDatabase: boolean;
  fileSize: number;
  importMethod: 'append' | 'replace' | 'map';
}

export type UndoType =
  | 'successfulImport'
  | 'entriesFiltered'
  | 'mismatchedFieldType'
  | 'restrictedContentCantBeCopied'
  | 'structureLocked'
  | 'updateFieldSettings';

export interface UndoEventData extends GenericEventData {
  source: 'flags' | 'snackbar';
  action: 'paste' | 'import';
  type: UndoType;
}

export interface ImportCancelledEventData extends GenericEventData {
  source: 'importDialogStepOne' | 'importDialogStepTwo';
}

export interface CellContentHasBeenModifiedEventData extends GenericEventData {
  activationId?: string;
  fieldType: DatabaseFieldType;
}

export interface ExportDatabaseEventData {
  applyViewOnExport: boolean;
  databaseId: string;
  databaseContainer: DatabaseContainerType;
  exportFormat: ExportFileType;
}

export interface HideColumnsEventData {
  contentId?: string;
  source: 'databaseView' | 'databaseViewSettingsPanel';
  databaseContainer: DatabaseContainerType;
  hidden: boolean;
  fieldType: DatabaseFieldType | null;
}

export interface HideAllColumnsEventData {
  contentId?: string;
  source: 'databaseViewSettingsPanel';
  databaseContainer: DatabaseContainerType;
  hidden: boolean;
}

export interface LayoutSelectedEventData extends GenericEventData {
  layoutType: DatabaseLayoutType;
  source: 'databaseViewSettingsPicker' | 'databaseViewSettingsPanel';
}

export interface LayoutSettingSelectedEventData extends GenericEventData {
  layoutSetting: 'cardWidth' | 'coverImage' | 'groupField' | 'dateField';
  source: 'databaseViewSettingsPicker' | 'databaseViewSettingsPanel';
  value?: string;
}

export interface NumberedEntriesToggledEventData extends GenericEventData {
  source: 'TableLayoutOptions';
  value: boolean;
}

export interface ReplaceDatabaseClickedEventData extends GenericEventData {
  source: 'datasourceDialog' | 'templates' | 'import';
}

export enum SendDatabaseSortEventSources {
  SORT_MENU = 'databaseSortMenu',
  FIELD_MENU = 'databaseFieldMenu',
  FIELD_NAME = 'databaseFieldName',
  VIEW_PANEL_MENU = 'databaseViewSettingsPanel',
}

export interface SendDatabaseSortEventData {
  contentId?: string;
  fieldId?: string; // undefined if resetting sort
  isAscending?: boolean; // undefined if resetting sort
  source: SendDatabaseSortEventSources;
  fieldType?: DatabaseFieldType;
  databaseContainer: DatabaseContainerType;
}

export interface SendDatabaseFilterData extends GenericEventData {
  fieldType?: DatabaseFieldType;
  filterType: DatabaseFilterType;
  source: 'databaseFilterMenu' | 'databaseFieldActions' | 'databaseViewSettingsPanel';
}

export interface SendDatabaseCreatedViewEventData extends ViewEventData {
  layoutType: DatabaseLayoutType;
}

export interface SendDatabaseEditViewEventData extends ViewEventData {
  source: 'databaseViewActionsMenu';
}

export enum SendDatabaseDeleteEntryEventSources {
  DATABASE_ENTRY_DIALOG = 'databaseEntryDialog',
  DELETE_ENTRY_CONFIRM_DIALOG = 'deleteEntryConfirmationDialog',
  ENTRY_CONTEXTUAL_MENU = 'entryContextualMenu',
  ACTION_BAR = 'actionBar',
}

export interface SendDeleteEntryEvent extends GenericEventData {
  source: SendDatabaseDeleteEntryEventSources;
}

export interface SendConnectionClickedEventData extends GenericEventData {
  connectionType: 'connectJira' | 'connectConfluence';
}

export interface SendCreateDatabaseEntryEventData extends GenericEventData {
  source: 'databaseView' | 'slashCommand';
}

export interface SendCreatePageFromDatabaseEventData extends GenericEventData {
  newPageId: string;
  parentId: string;
  pageLocation: ParentPageOption;
  templateId: string | null;
  hasEntryEmbed: boolean;
  pageSubType: PageSubType;
}

export interface SendCreateDatabaseFieldEventData extends GenericEventData {
  source: 'databaseCreateFieldDialog' | 'boardLayout' | 'calendarLayout';
}

export interface TemplateClickedEventData extends GenericEventData {
  isInitialDatabase: boolean;
  templateType:
    | 'productRequirements'
    | 'budget'
    | 'campaignTracker'
    | 'newHire'
    | 'contentLibrary'
    | 'contactLibrary'
    | 'researchRepository'
    | 'emptyDefault'
    | 'feedbackTracker'
    | 'meetingNotes'
    | 'capacityPlanning'
    | 'contentCalendar'
    | 'applicantTracker'
    | 'okrTracker'
    | 'vendorManagement'
    | 'legacyContentLibrary';
}

export interface ViewEventData extends GenericEventData {
  source:
    | 'databaseUnsavedViewChangesBanner'
    | 'databaseViewActionsMenu'
    | 'databaseViewPicker'
    | 'databaseViewSettingsPanel'
    | 'databaseToolbar'
    | 'databaseViewTabs';
  viewId: string;
  isEdited?: boolean;
}

export interface ViewUpdatedEventData extends ViewEventData {
  sortUpdated: boolean;
  filterUpdated: boolean;
  hideFieldsUpdated: boolean;
  renamed: boolean;
  selectedLayout: DatabaseLayoutType;
}

export interface SetUpPanelTabClickedEventData extends GenericEventData {
  isInitialDatabase: boolean;
  actionSubjectId: string;
}

export interface DatabaseCreateCalculationEventData extends GenericEventData {
  source: 'quickCalcFieldActions' | 'quickCalculationSelect';
  fieldId: string;
  calculationType: DatabaseCalculationType;
}

export interface DatabaseRemoveCalculationEventData extends GenericEventData {
  source:
    | 'quickCalcFieldActions'
    | 'quickCalculationSelect'
    | 'selectFieldMenu'
    | 'selectValueMenu'
    | 'deleteFieldButton';
  fieldId: string;
}

export interface DatabaseCalculationTypeConvertedData extends GenericEventData {
  source: 'quickCalcFieldActions' | 'quickCalculationSelect';
  fieldId: string;
  fromCalculationType: DatabaseCalculationType;
  toCalculationType: DatabaseCalculationType;
}

export interface EntryLinkFieldLoadData extends GenericEventData {
  source: 'fieldNameWithActions';
  fieldId: string;
}

export interface SendDatabaseContentViewedData extends GenericEventData {
  locale: string;
}

export interface FieldOptionEventData extends GenericEventData {
  source:
    | 'fieldSettingsDialog'
    | 'database'
    | 'createFieldDialog'
    | 'datasourceDialog'
    | 'boardLayout'
    | 'calendarLayout';
  fieldSelection: DatabaseFieldType;
  isNewField: boolean;
}

export interface PageCreationFieldSettingEditedEventData extends GenericEventData {
  fieldId: string;
  fieldSetting:
    | 'pageLocation'
    | 'parentItem'
    | 'pageTemplate'
    | 'displayEntryOnPage'
    | 'pageSubType';
  value: string | null;
}

export interface JiraIssueEventData extends GenericEventData {
  source: 'jiraIssueDetailsSettings';
  jiraFieldSelection: string;
}

export interface JiraFieldUpdatedEventData extends GenericEventData {
  issueKey: string;
  fieldType: 'status' | 'assignee' | 'priority' | 'summary';
}

export interface DatasourcesDBMigrationEventData extends GenericEventData {
  source: 'FieldNameWithActions';
  missingField: 'cloudId' | 'serverId';
  cloudId?: string;
  serverId?: string;
}

export interface DatabaseProfiledEventData extends GenericEventData {
  contentId: string;
  attributes: {
    entriesCount: number;
    fieldsCount: number;
    fieldTypeCounts: Record<FieldType, number>;
    fieldTypes: FieldType[];
    viewsCount: number;
    viewSettings: {
      layoutType: DatabaseLayoutType;
      showNumberedEntriesEnabled: boolean;
      cardWidth: string;
    };
  };
}

export interface SendMentionNotificationCheckboxToggledEvent extends GenericEventData {
  checked: boolean;
}

export interface MentionNotificationFlagViewedEventData extends GenericEventData {
  type: 'success' | 'restricted' | 'error';
}

export interface SendPageCreatedByFlowEventData extends GenericEventData {
  isLivePage: boolean;
}

type CheckboxState = 'selected' | 'unselected';
export interface SendRowCheckboxSelectedEventData extends GenericEventData {
  entryId: string;
  checkboxState: CheckboxState;
}

export interface SendHeaderCheckboxSelectedEventData extends GenericEventData {
  entriesCount: number;
  checkboxState: CheckboxState;
}

export interface SendSelectedActionItemEventData extends GenericEventData {
  //trigger ActionTypes like "move" | "add" | "delete" will not be sent
  type: ActionType;
  selectedCount: number;
  selectionMode: SelectionMode;
  entryId?: string;
}
